// Customers: every account in one paged list; an account opens in a sheet over the list with its plan,
// learners, orders and activity, and the admin actions (plan time, sign out, suspend, delete).
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { topicOf, unitOf } from '@mathladder/shared';
import { api, type Doc, type Student } from '../api';
import { ago, dollars, fmtDate, fmtDateTime, fmtSecs, money, plural, useAction, useLoad, usePaged, useTitle } from '../lib';
import { Alert, Button, Card, Empty, Facts, Field, Loading, Page, Pager, Search, Sheet, TableCard } from '../ui';
import { statusBadge } from './sales';

interface Row { id: string; email: string; name: string | null; created_at: string; last_login_at: string | null; email_verified_at: string | null; suspended_at: string | null; students: number; spent_cents: number; has_plan: boolean }

const PLAN_FILTERS: [string, string][] = [['', 'Everyone'], ['1', 'Unlimited'], ['0', 'Free']];

export function Customers() {
  useTitle('Customers');
  const { id } = useParams();
  const [p, setP] = useSearchParams();
  const nav = useNavigate();
  const q = p.get('q') ?? '', plan = p.get('plan') ?? '';
  const set = useCallback((next: Record<string, string>) => setP(Object.fromEntries(Object.entries({ q, plan, ...next }).filter(([, v]) => v))), [q, plan, setP]);
  const list = usePaged<Row>(`/admin/accounts?q=${encodeURIComponent(q)}${plan ? `&plan=${plan}` : ''}`, 'accounts');
  const open = (aid: string) => nav(`/customers/${aid}${location.search}`);

  return (
    <Page title="Customers" sub={list.rows ? `${list.total.toLocaleString()} ${plan === '1' ? 'with Unlimited' : plan === '0' ? 'without a plan' : 'accounts'}` : undefined}>
      <TableCard
        toolbar={<>
          <Search value={q} onSearch={(v) => set({ q: v })} placeholder="Email, name or learner" />
          <span className="seg" role="group" aria-label="Plan">
            {PLAN_FILTERS.map(([v, l]) => <button key={v} type="button" aria-pressed={plan === v} onClick={() => set({ plan: v })}>{l}</button>)}
          </span>
        </>}
        pager={list.rows && <Pager offset={list.offset} total={list.total} shown={list.rows.length} onPage={list.setOffset} />}
      >
        <Alert>{list.error}</Alert>
        {!list.rows ? <Loading /> : !list.rows.length ? <Empty>No accounts match.</Empty> : (
          <table>
            <thead><tr><th>Account</th><th>Plan</th><th className="num">Learners</th><th className="num">Spent</th><th>Joined</th><th>Last sign-in</th></tr></thead>
            <tbody>
              {list.rows.map((a) => (
                <tr key={a.id} className={`click${a.id === id ? ' on' : ''}`} onClick={() => open(a.id)}>
                  <td>
                    <Link to={`/customers/${a.id}${location.search}`} onClick={(e) => e.stopPropagation()}>{a.email}</Link>
                    {a.name && <div className="small muted">{a.name}</div>}
                  </td>
                  <td className="nowrap">
                    {a.has_plan ? <span className="badge badge-good">Unlimited</span> : <span className="badge">Free</span>}
                    {!a.email_verified_at && <span className="badge badge-warn" style={{ marginLeft: '.3rem' }}>unconfirmed</span>}
                    {a.suspended_at && <span className="badge badge-bad" style={{ marginLeft: '.3rem' }}>suspended</span>}
                  </td>
                  <td className="num">{a.students || ''}</td>
                  <td className="num">{a.spent_cents ? money(a.spent_cents) : ''}</td>
                  <td className="nowrap">{fmtDate(a.created_at)}</td>
                  <td className="nowrap muted">{a.last_login_at ? ago(a.last_login_at) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
      {id && <AccountSheet id={id} onClose={() => nav(`/customers${location.search}`)} onChanged={() => void list.reload()} />}
    </Page>
  );
}

interface Entitlement { id: string; student_id: string | null; student_name: string | null; source: string; note: string | null; starts_at: string; ends_at: string | null; revoked_at: string | null; revoked_reason: string | null }
interface Detail {
  account: { id: string; email: string; name: string | null; created_at: string; last_login_at: string | null; email_verified_at: string | null; suspended_at: string | null; suspended_reason: string | null; google: boolean; has_password: boolean };
  students: Student[];
  plan: { active: boolean; lifetime: boolean; endsAt: string | null; queuedUntil: string | null };
  entitlements: Entitlement[];
  orders: { id: string; receipt_no: number; status: string; total_cents: number; refunded_cents: number; created_at: string }[];
  documents: Doc[];
  attempts: { id: string; student_name: string; kind: string; stage: number; step: number | null; correct: number; total: number; seconds: number | null; outcome: string; created_at: string }[];
  sessions: { created_at: string; last_seen_at: string; expires_at: string; ip: string | null; user_agent: string | null }[];
}

const entState = (e: Entitlement) => (e.revoked_at ? 'revoked' : e.ends_at && new Date(e.ends_at) < new Date() ? 'ended' : new Date(e.starts_at) > new Date() ? 'upcoming' : 'active');
const place = (stage: number | null, step: number | null) => (stage ? `${unitOf(stage).name}${step ? `: ${topicOf(stage, step)?.name ?? ''}` : ''}` : '');

function AccountSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data, error, reload } = useLoad(() => api.get<Detail>(`/admin/accounts/${id}`), [id]);
  const done = async () => { await reload(); onChanged(); };
  const signOut = useAction(async () => { await api.post(`/admin/accounts/${id}/sign-out`); await done(); });
  const suspend = useAction(async () => {
    const reason = prompt('Why is this account being suspended? (kept in the audit log, not sent to the account holder)');
    if (!reason) return;
    await api.post(`/admin/accounts/${id}/suspend`, { reason });
    await done();
  });
  const reinstate = useAction(async () => { await api.post(`/admin/accounts/${id}/reinstate`); await done(); });
  const remove = useAction(async () => {
    const typed = prompt('Delete this account, its learners, progress and worksheets? Paid orders are kept for tax records without names. If a yearly plan has time left and this is not for a breach of the terms, refund the unused plan time from the order first.\n\nType the account\'s email to confirm:');
    if (!typed) return;
    await api.post(`/admin/accounts/${id}/delete`, { confirmEmail: typed });
    onChanged(); onClose();
  });
  const revoke = useAction(async (eid: string) => {
    const reason = prompt('Reason for revoking this plan time?');
    if (!reason) return;
    await api.post(`/admin/entitlements/${eid}/revoke`, { reason });
    await done();
  });

  if (error) return <Sheet title="Account" onClose={onClose}><Alert>{error}</Alert></Sheet>;
  if (!data) return <Sheet title="Account" onClose={onClose}><Loading /></Sheet>;
  const a = data.account, pl = data.plan;
  const spent = data.orders.filter((o) => !['pending', 'expired'].includes(o.status)).reduce((s, o) => s + o.total_cents - o.refunded_cents, 0);

  return (
    <Sheet
      title={a.email}
      sub={<>
        {pl.lifetime ? <span className="badge badge-good">Unlimited for life</span> : pl.active ? <span className="badge badge-good">Unlimited until {fmtDate(pl.queuedUntil ?? pl.endsAt!)}</span> : <span className="badge">Free</span>}
        {!a.email_verified_at && <span className="badge badge-warn">unconfirmed</span>}
        {a.suspended_at && <span className="badge badge-bad">suspended</span>}
        <span>Joined {fmtDate(a.created_at)}</span>
      </>}
      onClose={onClose}
    >
      {a.suspended_at && <Alert kind="warn">Suspended {fmtDateTime(a.suspended_at)}: {a.suspended_reason}. The account cannot sign in.</Alert>}
      <Alert>{signOut.error || revoke.error || suspend.error || reinstate.error || remove.error}</Alert>
      <Card>
        <div style={{ paddingTop: '1rem' }}>
          <Facts items={[
            ['Name', a.name], ['Spent', money(spent)], ['Learners', String(data.students.length)],
            ['Sign-in', [a.has_password && 'password', a.google && 'Google', 'email link'].filter(Boolean).join(', ')],
            ['Last sign-in', a.last_login_at ? fmtDateTime(a.last_login_at) : 'Never'],
            ['Active sessions', String(data.sessions.length)],
          ]} />
        </div>
        <div className="row" style={{ marginTop: '1rem', gap: '.4rem' }}>
          <Button className="btn btn-sm" busy={signOut.busy} disabled={!data.sessions.length} onClick={() => confirm('Sign this account out everywhere?') && void signOut.run()}>Sign out everywhere</Button>
          {a.suspended_at
            ? <Button className="btn btn-sm" busy={reinstate.busy} onClick={() => confirm('Reinstate this account so it can sign in again?') && void reinstate.run()}>Reinstate</Button>
            : <Button className="btn btn-sm btn-danger" busy={suspend.busy} onClick={() => void suspend.run()}>Suspend</Button>}
          <Button className="btn btn-sm btn-danger" busy={remove.busy} onClick={() => void remove.run()}>Delete account</Button>
        </div>
      </Card>

      <Card title="Plan time" sub="Purchases, codes and grants">
        <GivePlan accountId={a.id} onDone={done} />
        {data.entitlements.length > 0 && (
          <table className="table" style={{ marginTop: '1rem' }}>
            <thead><tr><th>Source</th><th>From</th><th>Until</th><th>State</th><th /></tr></thead>
            <tbody>
              {data.entitlements.map((e) => {
                const st = entState(e);
                return (
                  <tr key={e.id}>
                    <td>{e.student_name ? `${e.student_name} (old)` : e.source}<div className="small muted">{e.revoked_reason ?? e.note}</div></td>
                    <td className="nowrap">{fmtDate(e.starts_at)}</td>
                    <td className="nowrap">{e.ends_at ? fmtDate(e.ends_at) : 'Lifetime'}</td>
                    <td><span className={`badge ${st === 'active' ? 'badge-good' : st === 'revoked' ? 'badge-bad' : ''}`}>{st}</span></td>
                    <td>{!e.revoked_at && <button className="btn btn-quiet btn-sm" onClick={() => void revoke.run(e.id)}>Revoke</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Orders">
        {!data.orders.length ? <p className="small muted">No orders.</p> : (
          <table className="table">
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id}>
                  <td><Link to={`/sales/orders/${o.id}`}>#{o.receipt_no}</Link></td>
                  <td>{statusBadge(o.status)}</td>
                  <td className="num">{dollars(o.total_cents)}{o.refunded_cents ? <div className="small muted">−{dollars(o.refunded_cents)}</div> : null}</td>
                  <td className="nowrap muted">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Learners">
        {!data.students.length ? <p className="small muted">No learners.</p> : (
          <table className="table">
            <tbody>
              {data.students.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}{s.archived && <span className="badge" style={{ marginLeft: '.4rem' }}>archived</span>}<div className="small muted">{s.grade ? `Grade ${s.grade}` : ''}</div></td>
                  <td>{place(s.currentStage, null) || <span className="muted">Not placed</span>}</td>
                  <td className="nowrap muted">{fmtDate(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {data.attempts.length > 0 && (
        <Card title="Recorded results" sub={plural(data.attempts.length, 'result')}>
          <table className="table">
            <tbody>
              {data.attempts.slice(0, 30).map((t) => (
                <tr key={t.id}>
                  <td>{t.student_name}<div className="small muted">{place(t.stage, t.step)} · {t.kind}</div></td>
                  <td className="num">{t.correct}/{t.total}{t.seconds ? <div className="small muted">{fmtSecs(t.seconds)}</div> : null}</td>
                  <td>{t.outcome}</td>
                  <td className="nowrap muted">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Worksheets" sub={data.documents.length ? `Latest ${data.documents.length}` : undefined}>
        {!data.documents.length ? <p className="small muted">None yet.</p> : (
          <table className="table">
            <tbody>
              {data.documents.slice(0, 30).map((d) => (
                <tr key={d.id}>
                  <td>{d.title}</td>
                  <td><span className={`badge ${d.status === 'failed' ? 'badge-bad' : d.status === 'done' ? '' : 'badge-warn'}`}>{d.status}</span></td>
                  <td className="nowrap muted">{ago(d.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {data.sessions.length > 0 && (
        <Card title="Signed in on">
          <table className="table">
            <tbody>
              {data.sessions.map((s, i) => (
                <tr key={i}><td className="small">{s.user_agent?.slice(0, 70)}<div className="mono muted">{s.ip}</div></td><td className="nowrap muted">{ago(s.last_seen_at)}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Sheet>
  );
}

function GivePlan({ accountId, onDone }: { accountId: string; onDone: () => Promise<void> }) {
  const [f, setF] = useState({ days: '365', note: '' });
  const [ok, setOk] = useState<string | null>(null);
  const save = useAction(async () => {
    await api.post('/admin/entitlements', { accountId, days: f.days ? Number(f.days) : null, note: f.note });
    setOk(f.days ? `${f.days} days given.` : 'Lifetime given.');
    setF({ ...f, note: '' });
    await onDone();
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => { setOk(null); setF({ ...f, [k]: e.target.value }); };
  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }}>
      <div className="form-row" style={{ gridTemplateColumns: '7rem 1fr auto' }}>
        <Field label="Days" type="number" min={1} max={3650} placeholder="Lifetime" value={f.days} onChange={set('days')} />
        <Field label="Why" required maxLength={300} value={f.note} onChange={set('note')} placeholder="Support gesture" />
        <Button className="btn btn-primary" busy={save.busy}>Give</Button>
      </div>
      <p className="small muted" style={{ margin: '.4rem 0 0' }}>Added after any time the account already has. Leave days blank for lifetime.</p>
      <Alert>{save.error}</Alert>
      <Alert kind="good">{ok}</Alert>
    </form>
  );
}
